// FolderCard — 메인 콘텐츠 영역의 폴더 카드 컴포넌트 (UX v2 polish).
//
// 파일 탐색기(VS Code, Notion) 패턴으로 메인 목록에 자식 그룹을 폴더 카드로 표시.
// 클릭 → 해당 그룹으로 진입 (URL ?group={id}).
// 우클릭/⋯ 메뉴: 이름 변경·하위 그룹 추가·삭제.
//
// 표시 정보:
//  - 아이콘: lucide Folder/FolderOpen
//  - 이름
//  - 자식 항목 수 (예: "5 항목")
//  - 자식 그룹 수 (예: "3 폴더 · 5 항목")
//
// DnD: useDroppable로 드롭 수용. 드래그는 미지원 (추후 확장).

import { useCallback, useRef, useState } from 'react';
import { Folder, FolderOpen, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import type { GroupTreeNode } from '../api/groupsTree';
import { DropdownMenu, DropdownItem, DropdownSeparator } from '../ui/DropdownMenu';
import { IconButton } from '../ui/IconButton';
import { Tooltip } from '../ui/Tooltip';
import { useToast } from '../ui/Toast';
import { useUpdateGroup, useCreateGroup, useDeleteGroup } from '../hooks/useGroups';
import { InlineEditable } from './InlineEditable';
import type { DndDroppableData } from '../utils/dndHelpers';

// ─── 인터페이스 ───────────────────────────────────────────────

export interface FolderCardProps {
  /** 폴더 트리 노드 */
  node: GroupTreeNode;
  /** 클릭 시 해당 그룹으로 이동 */
  onNavigate: (groupId: number) => void;
  /** 이 폴더 내 항목 수 (선택) */
  itemCount?: number;
}

// ─── 컴포넌트 ─────────────────────────────────────────────────

/**
 * FolderCard — 메인 목록에 표시되는 폴더 카드.
 * 클릭으로 그룹 진입, 우측 ⋯ 메뉴로 관리.
 */
export function FolderCard({ node, onNavigate, itemCount }: FolderCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingChild, setIsAddingChild] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const { push: toastPush } = useToast();
  const updateGroup = useUpdateGroup();
  const createGroup = useCreateGroup();
  const deleteGroup = useDeleteGroup();

  // DnD — 드롭 수용 대상 등록
  const droppableData: DndDroppableData = {
    type: 'group',
    groupId: node.id,
    kind: node.kind,
  };

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `folder-card-${node.id}`,
    data: droppableData,
  });

  // ─── 이름 편집 ──────────────────────────────────────────────

  const handleSaveName = useCallback(
    async (newName: string) => {
      await updateGroup.mutateAsync({ id: node.id, patch: { name: newName } });
      setIsEditing(false);
      toastPush({ title: '이름이 변경되었습니다', variant: 'success', duration: 2000 });
    },
    [node.id, updateGroup, toastPush],
  );

  // ─── 하위 그룹 추가 ─────────────────────────────────────────

  const handleSaveChild = useCallback(
    async (name: string) => {
      await createGroup.mutateAsync({
        name,
        kind: node.kind,
        parentId: node.id,
      });
      setIsAddingChild(false);
      toastPush({ title: `'${name}' 그룹이 추가되었습니다`, variant: 'success', duration: 2000 });
    },
    [node.id, node.kind, createGroup, toastPush],
  );

  // ─── 그룹 삭제 ──────────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    try {
      await deleteGroup.mutateAsync({ id: node.id, cascade: true, groupName: node.name });
    } catch {
      toastPush({ title: '삭제에 실패했습니다', variant: 'error', duration: 3000 });
    }
  }, [node.id, node.name, deleteGroup, toastPush]);

  // ─── 표시 정보 계산 ──────────────────────────────────────────

  const childGroupCount = node.children.length;
  const hasChildGroups = childGroupCount > 0;

  // "3 폴더 · 5 항목" 또는 "5 항목" 형태
  const metaParts: string[] = [];
  if (hasChildGroups) {
    metaParts.push(`${childGroupCount} 폴더`);
  }
  if (itemCount !== undefined) {
    metaParts.push(`${itemCount} 항목`);
  }
  const metaText = metaParts.join(' · ');

  return (
    <div
      ref={(el) => {
        setDropRef(el);
        (cardRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }}
      className={[
        'folder-card',
        isOver ? 'folder-card--drop-target' : '',
        isHovered ? 'folder-card--hovered' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`folder-card-${node.id}`}
    >
      {/* 클릭 가능 영역 */}
      <button
        type="button"
        className="folder-card__body"
        onClick={() => {
          if (!isEditing) onNavigate(node.id);
        }}
        aria-label={`${node.name} 폴더 열기`}
      >
        {/* 폴더 아이콘 */}
        <span className="folder-card__icon" aria-hidden="true">
          {isHovered || isOver
            ? <FolderOpen size={32} className="folder-card__icon-svg folder-card__icon-svg--open" />
            : <Folder size={32} className="folder-card__icon-svg" />
          }
        </span>

        {/* 이름 영역 */}
        <div className="folder-card__info">
          {isEditing ? (
            <InlineEditable
              value={node.name}
              onSave={handleSaveName}
              onCancel={() => setIsEditing(false)}
              validate={(v) => v.trim().length > 0}
              autoFocus
              className="folder-card__inline-edit"
            />
          ) : (
            <span
              className="folder-card__name"
              onDoubleClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
            >
              {node.name}
            </span>
          )}
          {metaText && (
            <span className="folder-card__meta">{metaText}</span>
          )}
        </div>
      </button>

      {/* 액션 버튼 (hover 시 노출) */}
      {!isEditing && (
        <div className="folder-card__actions">
          <Tooltip content="하위 그룹 추가" side="top" sideOffset={4} delayDuration={600}>
            <IconButton
              icon={<Plus size={13} />}
              aria-label="하위 그룹 추가"
              variant="ghost"
              size="sm"
              className="folder-card__action-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsAddingChild(true);
              }}
            />
          </Tooltip>

          <DropdownMenu
            trigger={
              <Tooltip content="메뉴" side="top" sideOffset={4} delayDuration={600}>
                <IconButton
                  icon={<MoreHorizontal size={13} />}
                  aria-label="더 보기"
                  variant="ghost"
                  size="sm"
                  className="folder-card__action-btn"
                />
              </Tooltip>
            }
            align="end"
            sideOffset={4}
          >
            <DropdownItem
              icon={<Pencil size={13} />}
              onSelect={() => setIsEditing(true)}
            >
              이름 변경
            </DropdownItem>
            <DropdownItem
              icon={<Plus size={13} />}
              onSelect={() => setIsAddingChild(true)}
            >
              하위 그룹 추가
            </DropdownItem>
            <DropdownSeparator />
            <DropdownItem
              icon={<Trash2 size={13} />}
              variant="danger"
              onSelect={() => void handleDelete()}
            >
              삭제
            </DropdownItem>
          </DropdownMenu>
        </div>
      )}

      {/* 인라인 하위 그룹 추가 입력 — 카드 하단에 표시 */}
      {isAddingChild && (
        <div className="folder-card__add-child">
          <span className="folder-card__add-child-icon" aria-hidden="true">
            <Folder size={13} />
          </span>
          <InlineEditable
            value=""
            onSave={handleSaveChild}
            onCancel={() => setIsAddingChild(false)}
            validate={(v) => v.trim().length > 0}
            autoFocus
            placeholder="새 그룹 이름 (Esc로 취소)"
            className="folder-card__add-child-edit"
          />
        </div>
      )}
    </div>
  );
}

// ─── FolderCardList 섹션 컴포넌트 ────────────────────────────

export interface FolderCardListProps {
  /** 표시할 그룹 노드 배열 */
  nodes: GroupTreeNode[];
  /** 그룹 진입 콜백 */
  onNavigate: (groupId: number) => void;
  /** 각 그룹의 항목 수 맵 (group_id → count) */
  itemCountMap?: Record<number, number>;
}

/**
 * FolderCardList — 폴더 카드 그리드 섹션.
 * Explorer 메인 목록에서 자식 그룹들을 일반 항목 위에 표시.
 */
export function FolderCardList({ nodes, onNavigate, itemCountMap }: FolderCardListProps) {
  if (nodes.length === 0) return null;

  return (
    <section className="folder-card-list" aria-label="폴더">
      <div className="folder-card-list__grid">
        {nodes.map((node) => (
          <FolderCard
            key={node.id}
            node={node}
            onNavigate={onNavigate}
            itemCount={itemCountMap?.[node.id]}
          />
        ))}
      </div>
    </section>
  );
}
